import { useCallback, useMemo, useState } from 'react';
import { ContentAccessUser, UserProfile } from '@amzn/awscat-pattern-library-service-model';

import { extractUserAlias } from '../../api/UtilsApi';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

export interface AuthorRow {
  readonly userId: string;
  readonly userName: string;
  readonly alias: string | undefined;
  readonly isOwner: boolean;
}

export interface SelectedUser {
  readonly userId: string;
  readonly userName: string;
}

export interface AuthorsTabViewModel {
  readonly owner: AuthorRow;
  readonly coAuthorRows: readonly AuthorRow[];
  /** Stores the selected user from search without adding yet. */
  readonly selectUser: (userId: string, userName: string) => void;
  /** The currently selected user pending addition. */
  readonly selectedUser: SelectedUser | undefined;
  /** Persists the selected user as a co-author. */
  readonly addCoAuthor: () => void;
  /** True while the add API call is in-flight. */
  readonly isAdding: boolean;
  /** Error message from a failed add attempt. */
  readonly addError: string | undefined;
  /** Opens confirmation modal for the given co-author. */
  readonly removeCoAuthor: (userId: string) => void;
  readonly deleteConfirmation: DeleteConfirmationState | undefined;
  /** True while the removal API call is in-flight. */
  readonly isRemoving: boolean;
  /** Error message from a failed removal attempt. Shown inside the modal. */
  readonly removeError: string | undefined;
  readonly confirmRemove: () => void;
  readonly cancelRemove: () => void;
}

interface DeleteConfirmationState {
  readonly userId: string;
  readonly userName: string;
}

function resolveAlias(userId: string): string | undefined {
  const alias = extractUserAlias(userId);
  return alias !== userId ? alias : undefined;
}

function toOwnerRow(author: UserProfile): AuthorRow {
  return {
    userId: author.userId,
    userName: author.name,
    alias: resolveAlias(author.userId),
    isOwner: true,
  };
}

function toCoAuthorRow(user: ContentAccessUser): AuthorRow {
  return {
    userId: user.userId,
    userName: user.userName,
    alias: resolveAlias(user.userId),
    isOwner: false,
  };
}

export function useAuthorsTabViewModel(): AuthorsTabViewModel | undefined {
  const editorService = useContentEditorService();
  const coAuthors = useCoAuthors();
  const document = useLoadedDocument();

  // When author profile changes, recompute the owner row.
  const owner = useMemo(() => (document ? toOwnerRow(document.author) : undefined), [document]);

  // When coAuthors list changes, recompute display rows.
  const coAuthorRows = useMemo(() => coAuthors.map(toCoAuthorRow), [coAuthors]);

  const [selectedUser, setSelectedUser] = useState<SelectedUser | undefined>(undefined);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | undefined>(undefined);

  // When user picks a result from the search control, store it for the Add button.
  const selectUser = useCallback((userId: string, userName: string) => {
    setSelectedUser({ userId, userName });
    setAddError(undefined);
  }, []);

  // When user clicks the Add button, persist the selected user as a co-author.
  const addCoAuthor = useCallback(() => {
    if (!selectedUser) return;
    const current = editorService.coAuthors;
    if (current.some((u) => u.userId === selectedUser.userId)) return;
    if (document?.author.userId === selectedUser.userId) return;

    setIsAdding(true);
    setAddError(undefined);
    editorService
      .addCoAuthor(ContentAccessUser.create(selectedUser.userId, selectedUser.userName, ''))
      .then(() => {
        setSelectedUser(undefined);
      })
      .catch(() => {
        setAddError('Failed to add co-author. Please try again.');
      })
      .finally(() => {
        setIsAdding(false);
      });
  }, [selectedUser, editorService, document?.author.userId]);

  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState | undefined>(
    undefined,
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);

  // When user clicks remove on a co-author row, open the confirmation modal.
  const removeCoAuthor = useCallback(
    (userId: string) => {
      const user = editorService.coAuthors.find((u) => u.userId === userId);
      setDeleteConfirmation({ userId, userName: user?.userName ?? userId });
      setRemoveError(undefined);
    },
    [editorService],
  );

  // When user confirms removal, call the API and show spinner until done.
  const confirmRemove = useCallback(() => {
    if (!deleteConfirmation) return;
    setIsRemoving(true);
    setRemoveError(undefined);
    editorService
      .removeCoAuthor(deleteConfirmation.userId)
      .then(() => {
        setDeleteConfirmation(undefined);
      })
      .catch(() => {
        setRemoveError('Failed to remove co-author. Please try again.');
      })
      .finally(() => {
        setIsRemoving(false);
      });
  }, [deleteConfirmation, editorService]);

  // When user dismisses the modal, close it without changes.
  const cancelRemove = useCallback(() => {
    setDeleteConfirmation(undefined);
    setRemoveError(undefined);
  }, []);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document || !owner) return undefined;

  return {
    owner,
    coAuthorRows,
    selectUser,
    selectedUser,
    addCoAuthor,
    isAdding,
    addError,
    removeCoAuthor,
    deleteConfirmation,
    isRemoving,
    removeError,
    confirmRemove,
    cancelRemove,
  };
}
